import { extend } from 'vee-validate';
// eslint-disable-next-line @typescript-eslint/camelcase
import { required, is, email, digits, min_value, max_value, numeric } from 'vee-validate/dist/rules';

const registerValidators = () => {
	extend('required', {
		...required,
		message: (field) => {
			return `Bitte füllen Sie das Feld "${field}" aus`;
		},
	});
	extend('is', is);
	extend('email', {
		...email,
		message: () => {
			return `Bitte überprüfen Sie Ihre Eingabe.`;
		},
	});
	extend('digits', {
		...digits,
		message: () => {
			return `Bitte überprüfen Sie Ihre Eingabe.`;
		},
	});
	extend('min_value', {
		// eslint-disable-next-line @typescript-eslint/camelcase
		...min_value,
		message: (field, params) => {
			return `Die Eingabe darf nicht kleiner als ${params.min} sein.`;
		},
	});
	extend('max_value', {
		// eslint-disable-next-line @typescript-eslint/camelcase
		...max_value,
		message: (field, params) => {
			return `Die Eingabe darf nicht grösser als ${params.max} sein.`;
		},
	});
	extend('numeric', numeric);
};

const isValidAccountNumber = (str: string) => {
	const splitAccNo = str.match(/^([0-9]{7})([0-9]{1})([0-9]{2})$/);
	if (!splitAccNo || str.length !== 10) {
		return false;
	}
	const weights = [2, 1, 2, 1, 2, 1, 2];
	let checksum = 0,
		intermediateSum: number;
	for (let i = 0; i < splitAccNo[1].length; i++) {
		// weight every entry of the first seven digits
		intermediateSum = parseInt(splitAccNo[1][i]) * weights[i];
		// if the weighted entry has 2 digits --> use the digit sum instead
		// e.g. 14 will become 1+4=5
		if (intermediateSum > 9) {
			intermediateSum = intermediateSum
				.toString()
				.split('')
				.map(Number)
				.reduce(function(a, b) {
					return a + b;
				}, 0);
		}
		checksum += intermediateSum;
	}
	let differenceToNextTen = 10 - (checksum % 10);
	differenceToNextTen === 10 && (differenceToNextTen = 0);
	// check, that the calculated check digit equals the actual check digit
	return differenceToNextTen === parseInt(splitAccNo[2]);
};

const mod97 = (iban: string) => {
	let checksum: number | string = iban.slice(0, 2);
	let fragment;

	for (let offset = 2; offset < iban.length; offset += 7) {
		fragment = String(checksum) + iban.substring(offset, offset + 7);
		checksum = parseInt(fragment, 10) % 97;
	}
	return checksum;
};

const isValidIBAN = (iban: string) => {
	iban = iban.replace(/ /g, '');

	const numericIban = iban.slice(4) + iban.slice(0, 4);
	const digits = numericIban
		.split('')
		.map((char) => (isNaN(parseInt(char, 10)) ? char.charCodeAt(0) - 55 : char))
		.join('');
	const remainder = mod97(digits);
	return remainder === 1;
};

extend('verifyAccountOrIban', (input) => {
	input = input.replace(/\s+/g, '');
	let result = false;
	if (input.length === 22) {
		result = isValidIBAN(input);
	}
	if (input.length === 10) {
		result = isValidAccountNumber(input);
	}
	if (input.length === 16) {
		result = new RegExp('^([0-9]){16,}$').test(input);
	}
	const specialChars = /[`!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?~§]/;
	// don't allow any special characters except letters, numbers and space
	result = result && !specialChars.test(input);
	return (
		result ||
		'Bitte überprüfen Sie Ihre Eingabe: Ihre Kontonummer bzw. Kundennummer muss 10 Stellen enthalten, Ihre IBAN muss 22 Stellen enthalten'
	);
});

extend('minDate', {
	params: ['min'],
	validate(value, { min }: Record<string, any>) {
		return (
			Date.parse(value) >= Number(min) ||
			`Bitte überprüfen Sie Ihre Eingabe: Das Datum darf nicht vor dem ${new Date(Number(min)).toLocaleDateString(
				'de',
			)} liegen.`
		);
	},
});

extend('maxDate', {
	params: ['max'],
	validate(value, { max }: Record<string, any>) {
		return (
			Date.parse(value) <= Number(max) ||
			`Bitte überprüfen Sie Ihre Eingabe: Das Datum darf nicht hinter dem ${new Date(
				Number(max),
			).toLocaleDateString('de')} liegen.`
		);
	},
});

export default registerValidators;
